import React, { useEffect, useRef, useState } from 'react';
import useCancelOrder from '../../utils/useCancelOrder';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useSnackbar } from 'notistack';
import { Link, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import MuiLink from '@material-ui/core/Link';
import Tooltip from '@material-ui/core/Tooltip';
import CancelOrderProgressDialog from '../../components/CancelOrderProgressDialog';
import ForceCancelOrderDialog from '../../components/ForceCancelOrderDialog';
import OrderCommunication from './OrderCommunication';
import OrderDetails from './OrderDetails';
import OrderItems from './OrderItems';
import OrderLogs from './OrderLogs';
import OrderProvider from '../../components/OrderProvider';
import DataFlowDrawer from '@afosto/app-components-react/DataFlowDrawer';
import Dialog from '@afosto/app-components-react/Dialog';
import EmailDrawer from '@afosto/app-components-react/EmailDrawer';
import Icon from '@afosto/app-components-react/Icon';
import Page from '@afosto/app-components-react/Page';
import ProgressDialog from '@afosto/app-components-react/ProgressDialog';
import SkeletonPage from '@afosto/app-components-react/SkeletonPage';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import getFullName from '@afosto/utils/getFullName';
import pollTask from '@afosto/utils-react/tasks/pollTask';
import { addresses as addressesApi } from '@afosto/contacts/api/Addresses';
import { calculations as calculationsApi } from '@afosto/order/api/Calculations';
import { contacts as contactsApi } from '@afosto/contacts/api/Contacts';
import { items as itemsApi } from '@afosto/order/api/Items';
import { oauth as oauthApi } from '@afosto/iam/api/Oauth';
import { orders as ordersApi } from '@afosto/order/api/Orders';
import { organisations as organisationsApi } from '@afosto/contacts/api/Organisations';
import { session as sessionApi } from '@afosto/order/api/Session';
import translations from './translations';

const CHECKOUT_APP_URL = 'https://checkout.afosto.nl';

const OrderView = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:orders:read']);

  const intl = useIntl();
  const confirm = useConfirm();
  const { orderID } = useParams();
  const history = useHistory();
  const location = useLocation();

  const previewContainer = useRef(null);
  const [order, setOrder] = useState(null);
  const [isFetchingOrder, setIsFetchingOrder] = useState(true);
  const [itemCount, setItemCount] = useState(0);
  const [calculation, setCalculation] = useState(null);
  const [calculations, setCalculations] = useState([]);
  const [isFetchingCalculation, setIsFetchingCalculation] = useState(true);
  const [client, setClient] = useState(null);
  const [addressSet, setAddressSet] = useState(null);
  const [isFetchingAddress, setIsFetchingAddress] = useState(true);
  const [billingAddress, setBillingAddress] = useState(null);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [contact, setContact] = useState(null);
  const [isFetchingContact, setIsFetchingContact] = useState(true);
  const [organisation, setOrganisation] = useState(null);
  const [isFetchingOrganisation, setIsFetchingOrganisation] = useState(true);
  const [isFetchingStacks, setIsFetchingStacks] = useState(true);
  const [isAllocatingOrder, setIsAllocatingOrder] = useState(false);
  const [isStartingOrderFulfilment, setIsStartingOrderFulfilment] = useState(false);
  const [shipmentIds, setShipmentIds] = useState([]);
  const [orderIds, setOrderIds] = useState([]);
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [session, setSession] = useState(null);
  const [hasCopiedUrl, setHasCopiedUrl] = useState(false);
  const [projection, setProjection] = useState(null);
  const [showEmailDrawer, setShowEmailDrawer] = useState(false);
  const [showDataFlowDrawer, setShowDataFlowDrawer] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const {
    cancelOrder,
    clearForceCancel,
    forceCancelOrder,
    isCancellingOrder,
    showForceCancelOrder,
    warnings,
  } = useCancelOrder(order);

  const email = {
    bcc: [],
    cc: [],
    folder: 'concept',
    html_body: `<a href="${CHECKOUT_APP_URL}?id=${session?.id}">${CHECKOUT_APP_URL}?id=${session?.id}</a>`,
    subject: `Betaallink order ${order?.number}`,
    to: [{ name: contact ? getFullName(contact) : null, email: contact?.email }],
    attachments: calculation ? [calculation?.file_id] : [],
  };

  /**
   * Handle delete order.
   * @returns {Promise<void>}
   */
  const handleDeleteOrder = async () => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.confirmDeleteOrder.title),
        description: intl.formatMessage(translations.confirmDeleteOrder.description),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.confirmDeleteOrder.buttons.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.confirmDeleteOrder.buttons.delete),
            response: 'deleteOrder',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse !== 'deleteOrder') {
        return;
      }

      await ordersApi.deleteOrder(order.id);

      history.push('/orders');

      enqueueSnackbar(intl.formatMessage(translations.feedback.orderDeleted), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.orderNotDeleted), {
        variant: 'error',
      });
    }
  };

  /**
   * Handle confirm order.
   * @returns {Promise<void>}
   */
  const handleConfirmOrder = async () => {
    try {
      await ordersApi.confirmOrder(order.id);

      setOrder({ ...order, state: { ...order.state, is_confirmed: true, is_accepted: true } });

      enqueueSnackbar(intl.formatMessage(translations.feedback.orderConfirmed), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.orderNotConfirmed), {
        variant: 'error',
      });
    }
  };

  /**
   * Handle accept order.
   * @returns {Promise<void>}
   */
  const handleAcceptOrder = async () => {
    try {
      await ordersApi.acceptOrder(order.id);

      setOrder({ ...order, state: { ...order.state, is_accepted: true } });

      enqueueSnackbar(intl.formatMessage(translations.feedback.orderAccepted), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.orderNotAccepted), {
        variant: 'error',
      });
    }
  };

  const handleAllocateOrder = async () => {
    try {
      setIsAllocatingOrder(true);

      const response = await ordersApi.allocateOrder(order.id);
      const taskID = response?.headers?.['x-task-id'];

      if (taskID) {
        await pollTask(taskID, 10);
      }

      enqueueSnackbar(intl.formatMessage(translations.feedback.orderAllocated), {
        variant: 'success',
      });
    } catch (error) {
      const errorMessage =
        error?.result && typeof error?.result === 'string'
          ? error.result
          : intl.formatMessage(translations.feedback.orderFulfilmentNotStarted);

      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
    } finally {
      setIsAllocatingOrder(false);
    }
  };

  const handleStartOrderFulfilment = async () => {
    try {
      setIsStartingOrderFulfilment(true);

      const response = await ordersApi.fulfilOrder(order.id);
      const taskID = response?.headers?.['x-task-id'];

      if (taskID) {
        await pollTask(taskID, 10);
      }

      enqueueSnackbar(intl.formatMessage(translations.feedback.orderFulfilmentStarted), {
        variant: 'success',
      });
    } catch (error) {
      const errorMessage =
        error?.result && typeof error?.result === 'string'
          ? error.result
          : intl.formatMessage(translations.feedback.orderFulfilmentNotStarted);

      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
    } finally {
      setIsStartingOrderFulfilment(false);
    }
  };

  const handleStartCheckout = async () => {
    try {
      const today = new Date();
      const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

      const response = await sessionApi.startSession({
        expires_at: nextWeek,
        audience: 'afosto.com',
      });
      const { data: sessionData } = response;

      await sessionApi.attachSession(sessionData?.id, {
        order_id: order?.id,
      });

      setSession(sessionData);
      setShowSessionDialog(true);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.checkoutNotStarted), {
        variant: 'error',
      });
    }
  };

  const fetchOrder = async orderId => {
    setIsFetchingOrder(true);

    try {
      const response = await ordersApi.getOrder(orderId);
      const { data = null } = response;

      setOrder(data);
    } catch (error) {
      setOrder(null);
    } finally {
      setIsFetchingOrder(false);
    }
  };

  const handleCancelOrder = async () => {
    try {
      await cancelOrder();
      await fetchOrder(order?.id);
    } catch (err) {
      // Do nothing.
    }
  };

  const handleForceCancelOrder = async () => {
    try {
      await forceCancelOrder();
      await fetchOrder(order?.id);
    } catch (err) {
      // Do nothing.
    }
  };

  const handleCreateEmail = async changedEmail => {
    try {
      if (changedEmail?.folder !== 'concept') {
        enqueueSnackbar(intl.formatMessage(translations.feedback.emailSent), {
          variant: 'success',
        });
        setShowEmailDrawer(false);
      }
    } catch (err) {
      if (changedEmail?.folder !== 'concept') {
        enqueueSnackbar(intl.formatMessage(translations.feedback.emailNotSent), {
          variant: 'error',
        });
      }
    }
  };

  const handleCloseEmailDrawer = changedEmail => {
    setShowEmailDrawer(false);

    try {
      if (!changedEmail) {
        return;
      }

      if (changedEmail.folder === 'concept') {
        enqueueSnackbar(intl.formatMessage(translations.feedback.emailConceptSaved), {
          variant: 'success',
        });
        setShowEmailDrawer(false);
      }
    } catch (err) {
      if (changedEmail?.folder === 'concept') {
        enqueueSnackbar(intl.formatMessage(translations.feedback.emailConceptNotSaved), {
          variant: 'error',
        });
      }
    }
  };

  const handleTriggerDataFlow = () => setShowDataFlowDrawer(true);

  useEffect(() => {
    if (orderID) {
      fetchOrder(orderID);
    }
  }, [orderID]);

  useEffect(() => {
    let isMounted = true;

    const fetchClient = async clientId => {
      try {
        const response = await oauthApi.getClient(clientId);
        const { data = null } = response;

        if (isMounted) {
          setClient(data);
        }
      } catch (err) {
        if (isMounted) {
          setClient(null);
        }
      }
    };

    const fetchProjection = async () => {
      try {
        const response = await ordersApi.getOrderProjection(order?.id);
        const { data = null } = response ?? {};

        if (isMounted) {
          setProjection(data);
        }
      } catch (err) {
        if (isMounted) {
          setProjection(null);
        }
      }
    };

    const fetchCalculations = async () => {
      setIsFetchingCalculation(true);

      try {
        const orderCalculations = order?.calculations ?? [];
        const promises = orderCalculations.map(orderCalculation =>
          calculationsApi.getCalculation(orderCalculation?.id),
        );
        const responses = await Promise.allSettled(promises).then(result =>
          result.filter(promise => promise.status === 'fulfilled'),
        );

        const formattedCalculations = responses.reduce((acc, response) => {
          const calculationData = response?.value?.data;

          if (calculationData) {
            const orderCalculation = findObjectInArray(
              orderCalculations,
              'id',
              calculationData?.id,
            );

            return [
              ...acc,
              {
                ...calculationData,
                acceptances: orderCalculation?.acceptances ?? [],
                is_valid: orderCalculation?.is_valid ?? false,
                is_accepted: orderCalculation?.is_accepted ?? false,
                is_confirmed: orderCalculation?.is_confirmed ?? false,
              },
            ];
          }

          return acc;
        }, []);

        const validCalculations = formattedCalculations.filter(
          formattedCalculation => formattedCalculation.is_valid,
        );
        const [lastCalculation] = (validCalculations?.length > 0
          ? validCalculations
          : formattedCalculations
        ).reverse();

        if (isMounted && lastCalculation) {
          setCalculation(lastCalculation);
        }

        if (isMounted) {
          setCalculations(formattedCalculations);
        }
      } catch (err) {
        if (isMounted) {
          setCalculation(null);
          setCalculations([]);
        }
      } finally {
        if (isMounted) {
          setIsFetchingCalculation(false);
        }
      }
    };

    const fetchContact = async contactID => {
      if (!contactID) {
        setContact(null);
        setIsFetchingContact(false);
        return;
      }

      setIsFetchingContact(true);

      try {
        const response = await contactsApi.getContact(contactID);
        const { data = {} } = response;

        if (isMounted) {
          setContact(data);
        }
      } catch (error) {
        if (isMounted) {
          setContact(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingContact(false);
        }
      }
    };

    const fetchOrganisation = async organisationID => {
      if (!organisationID) {
        setOrganisation(null);
        setIsFetchingOrganisation(false);
        return;
      }

      setIsFetchingOrganisation(true);

      try {
        if (!organisationID) {
          setIsFetchingOrganisation(false);
          return;
        }

        const response = await organisationsApi.viewOrganisation(organisationID);
        const { data } = response;

        if (isMounted) {
          setOrganisation(data);
        }
      } catch (error) {
        if (isMounted) {
          setOrganisation(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingOrganisation(false);
        }
      }
    };

    const fetchAddresses = async (billingAddressID, shipmentAddressID) => {
      setIsFetchingAddress(true);

      try {
        if (billingAddressID) {
          const billingAddressResponse = await addressesApi.viewAddress(billingAddressID);
          const { data: billingAddressData } = billingAddressResponse;

          if (isMounted) {
            setBillingAddress(billingAddressData);
          }
        } else if (isMounted) {
          setBillingAddress(null);
        }

        if (shipmentAddressID) {
          const shipmentAddressResponse = await addressesApi.viewAddress(shipmentAddressID);
          const { data: shipmentAddressData } = shipmentAddressResponse;

          if (isMounted) {
            setShippingAddress(shipmentAddressData);
          }
        } else if (isMounted) {
          setShippingAddress(null);
        }
      } catch (err) {
        if (isMounted) {
          setBillingAddress(null);
          setShippingAddress(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingAddress(false);
        }
      }
    };

    const fetchStacks = async () => {
      try {
        setIsFetchingStacks(true);

        const response = await ordersApi.getOrderStacks(order?.id);
        const { data: stacks = [] } = response;

        const stackShipmentIds = stacks
          .filter(({ entity_type: type }) =>
            ['shipment', 'delivery', 'collect', 'inboundShipment', 'outboundShipment'].includes(
              type,
            ),
          )
          .map(({ entity_id: id }) => id);

        if (isMounted) {
          setShipmentIds(stackShipmentIds);
        }
      } catch (err) {
        if (isMounted) {
          setShipmentIds([]);
        }
      } finally {
        if (isMounted) {
          setIsFetchingStacks(false);
        }
      }
    };

    const fetchOrders = async () => {
      try {
        const response = await ordersApi.getOrderReferences(order?.id);
        const { data = [] } = response;

        if (isMounted) {
          setOrderIds(data.map(orderData => orderData.id));
        }
      } catch (err) {
        if (isMounted) {
          setOrderIds([]);
        }
      }
    };

    const fetchOrderItemCount = async () => {
      try {
        const response = await itemsApi.listItems(
          order?.id,
          {},
          {
            headers: {
              'x-page-size': 1,
            },
          },
        );

        if (isMounted && response?.headers?.['x-count']) {
          setItemCount(parseInt(response?.headers?.['x-count'], 10));
        } else if (isMounted) {
          setItemCount(0);
        }
      } catch (err) {
        if (isMounted) {
          setItemCount(0);
        }
      }
    };

    if (order) {
      fetchOrderItemCount();
      fetchClient(order?.client_id);
      fetchContact(order?.settings?.contact?.id);
      fetchOrganisation(order?.settings?.organisation?.id);
      fetchAddresses(order?.settings?.billing?.address_id, order?.settings?.shipping?.address_id);
      fetchCalculations();
      fetchStacks();
      fetchOrders();
      fetchProjection();
    }

    return () => {
      isMounted = false;
    };
  }, [order]);

  useEffect(() => {
    let isMounted = true;
    let timer;

    if (hasCopiedUrl) {
      timer = setTimeout(() => {
        if (isMounted) {
          setHasCopiedUrl(false);
        }
      }, 2000);
    }

    return () => {
      isMounted = false;

      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [hasCopiedUrl]);

  if (!hasPermissions()) {
    return (
      <SkeletonPage fullWidth breadcrumbs={2} subNavigation={3}>
        <UnauthorizedComponent />
      </SkeletonPage>
    );
  }

  if (isFetchingOrder) {
    return (
      <SkeletonPage fullWidth breadcrumbs={2} subNavigation={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8} xl={9}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={4} xl={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </SkeletonPage>
    );
  }

  if (!order) {
    return (
      <SkeletonPage fullWidth breadcrumbs={2} subNavigation={3}>
        Order not found
      </SkeletonPage>
    );
  }

  const secondaryActions = [
    ...(!order?.state?.is_confirmed &&
    order?.state?.is_accepted &&
    hasPermissions(['odr:orders:write'])
      ? [
          {
            text: intl.formatMessage(translations.secondaryActions.confirmOrder),
            action: handleConfirmOrder,
            shortCut: true,
            icon: ['fal', 'check-double'],
          },
        ]
      : []),
    ...(!order?.state?.is_accepted && hasPermissions(['odr:orders:write'])
      ? [
          {
            text: intl.formatMessage(translations.secondaryActions.acceptOrder),
            action: handleAcceptOrder,
            shortCut: true,
            icon: ['fal', 'check'],
          },
        ]
      : []),
    {
      text: intl.formatMessage(translations.secondaryActions.triggerDataFlow),
      action: handleTriggerDataFlow,
    },
    ...(order?.state?.is_accepted && hasPermissions(['odr:orders:write'])
      ? [
          {
            text: intl.formatMessage(translations.secondaryActions.allocateOrder),
            action: handleAllocateOrder,
          },
          {
            text: intl.formatMessage(translations.secondaryActions.startOrderFulfilment),
            action: handleStartOrderFulfilment,
          },
        ]
      : []),
    ...(!order?.state?.is_finished && (!projection || (projection && projection?.total >= 0))
      ? [
          {
            text: intl.formatMessage(translations.secondaryActions.startCheckout),
            action: handleStartCheckout,
          },
        ]
      : []),
    {
      text: intl.formatMessage(translations.secondaryActions.cancelOrder),
      action: handleCancelOrder,
      color: 'secondary',
    },
    ...(!order?.state?.is_confirmed && hasPermissions(['odr:orders:write'])
      ? [
          {
            text: intl.formatMessage(translations.secondaryActions.deleteOrder),
            action: handleDeleteOrder,
            color: 'secondary',
          },
        ]
      : []),
  ];

  return (
    <OrderProvider
      value={{
        order,
        setOrder,
        calculation,
        calculations,
        isFetchingCalculation,
        client,
        addressSet,
        setAddressSet,
        isFetchingAddress,
        billingAddress,
        shippingAddress,
        contact,
        isFetchingContact,
        organisation,
        isFetchingOrganisation,
        isFetchingStacks,
        projection,
        shipmentIds,
        orderIds,
        itemCount,
        fetchOrder,
        previewContainer,
      }}
    >
      <Grid container wrap="nowrap">
        <Page
          title={order?.number ?? 'Concept'}
          fullWidth
          subNavigation={{
            current: location.pathname,
            tabs: [
              {
                label: intl.formatMessage(translations.subNavigation.details),
                path: `/orders/${orderID}`,
              },
              {
                label: intl.formatMessage(translations.subNavigation.items),
                path: `/orders/${orderID}/items`,
              },
              {
                label: intl.formatMessage(translations.subNavigation.communication),
                path: `/orders/${orderID}/communication`,
              },
              {
                label: intl.formatMessage(translations.subNavigation.logs),
                path: `/orders/${orderID}/logs`,
              },
            ],
            onChange: (e, value) => history.replace(value),
          }}
          breadcrumbs={[
            { label: intl.formatMessage(translations.breadcrumbs.orders), path: '/' },
            { label: order?.number ?? 'Concept', path: `/orders/${orderID}`, active: true },
          ]}
          linkComponent={Link}
          primaryAction={
            !order?.state?.is_confirmed &&
            (location.pathname === `/orders/${orderID}` ||
              location.pathname === `/orders/${orderID}/items`)
              ? {
                  text: intl.formatMessage(translations.primaryAction.label),
                  action: () => {
                    history.push(`/orders/edit/${order.id}`);
                  },
                  icon: ['fal', 'pencil'],
                }
              : null
          }
          secondaryActions={secondaryActions}
        >
          <Switch>
            <Route component={OrderLogs} path="/orders/:orderID/logs" />
            <Route component={OrderItems} path="/orders/:orderID/items" />
            <Route component={OrderCommunication} path="/orders/:orderID/communication" />
            <Route component={OrderDetails} path="/orders/:orderID" />
          </Switch>
        </Page>
        <div ref={previewContainer} />
        <Dialog open={showSessionDialog} onClose={() => setShowSessionDialog(false)}>
          <DialogTitle>{intl.formatMessage(translations.sessionDialog.title)}</DialogTitle>
          <DialogContent>
            <Box display="flex" alignItems="flex-start" flex={1}>
              <MuiLink
                variant="subtitle1"
                href={`${CHECKOUT_APP_URL}?id=${session?.id}`}
                target="_blank"
                style={{ wordBreak: 'break-all' }}
              >
                https://checkout.afosto.nl?id={session?.id}
              </MuiLink>
              {session && navigator?.clipboard?.writeText && (
                <>
                  <Tooltip
                    title={intl.formatMessage(
                      hasCopiedUrl
                        ? translations.sessionDialog.fields.sessionUrl.tooltip.copied
                        : translations.sessionDialog.fields.sessionUrl.tooltip.copy,
                    )}
                  >
                    <IconButton
                      edge="end"
                      aria-label={intl.formatMessage(
                        translations.sessionDialog.fields.sessionUrl.button,
                      )}
                      onClick={() => {
                        navigator.clipboard.writeText(`${CHECKOUT_APP_URL}?id=${session?.id}`);
                        setHasCopiedUrl(true);
                      }}
                    >
                      <Icon icon={['fal', 'copy']} />
                    </IconButton>
                  </Tooltip>
                  {hasPermissions(['mes:emails:write', 'mes:inboxes:read']) && (
                    <Tooltip
                      title={intl.formatMessage(
                        translations.sessionDialog.fields.sessionUrl.tooltip.email,
                      )}
                    >
                      <IconButton
                        edge="end"
                        aria-label={intl.formatMessage(
                          translations.sessionDialog.fields.sessionUrl.tooltip.email,
                        )}
                        onClick={() => {
                          setShowEmailDrawer(true);
                        }}
                        style={{ marginLeft: 8 }}
                      >
                        <Icon icon={['fal', 'envelope']} />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSessionDialog(false)} color="secondary">
              {intl.formatMessage(translations.sessionDialog.actions.close)}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
      <EmailDrawer
        email={email}
        isOpen={showEmailDrawer}
        onClose={handleCloseEmailDrawer}
        onSubmit={handleCreateEmail}
      />
      <DataFlowDrawer
        open={showDataFlowDrawer}
        filter="order"
        onClose={() => setShowDataFlowDrawer(false)}
        onFinish={() => setShowDataFlowDrawer(false)}
        entityId={orderID}
      />
      <CancelOrderProgressDialog isOpen={isCancellingOrder} />
      <ForceCancelOrderDialog
        isOpen={showForceCancelOrder}
        onClose={clearForceCancel}
        onContinue={handleForceCancelOrder}
        warnings={warnings}
      />
      <ProgressDialog
        isOpen={isAllocatingOrder}
        message={intl.formatMessage(translations.feedback.allocatingOrder)}
      />
      <ProgressDialog
        isOpen={isStartingOrderFulfilment}
        message={intl.formatMessage(translations.feedback.startOrderFulfilment)}
      />
    </OrderProvider>
  );
};

export default OrderView;
